import React, { useState, useEffect, useCallback } from "react";
import { useParams, useLocation, useNavigate, Link } from "react-router-dom";
import Helmet from "react-helmet";
import axios from "axios";
import { MDBContainer, MDBNavbar, MDBNavbarBrand } from "mdb-react-ui-kit";
import Meta from "../../Components/Meta";
import Language from "../../Components/Languages";
import * as dataActivation from "../../Data/Activation.json";
import * as dataSupport from "../../Data/TechnicalSupport.json";
import {
  lireActivation, sauverActivation, effacerActivation, sessionAcademie,
  JETON_RE, SERIE_PROCONNECT_RE,
} from "./activationEnAttente";
import "./Activation.scss";

// Page /:lang/activer — rattachement d'un Pro connect converti par le QR de son
// ecran (jeton de possession), puis procedure de mise a jour du module coeur.
//   etape "connexion"  : pas de session -> connexion / creation de compte (ecrans
//                        existants) ; sn et t sont gardes (activationEnAttente.js)
//   etape "attente"    : POST /Back-end/claimDevice en cours
//   etape "succes"     : procedure DFU + telechargement direct + envoi par mail
//   etape "refus"      : message clair selon la raison du serveur
//   etape "manuel"     : QR sans jeton -> ajout par numero de serie (addDevice existant)
const LANGUES = ["fr", "en", "es"];
const VIDEO = "https://www.youtube.com/watch?v=GYXWUOeNC7s";
const SAV = "https://forms.office.com/r/4MWeCBrWZA";
const APP_IOS = "https://apps.apple.com/app/nrf-toolbox/id820906058";
const APP_ANDROID = "https://play.google.com/store/apps/details?id=no.nordicsemi.android.dfu";
// Repli si le serveur ne renvoie pas zip_url (parcours sans jeton).
const ZIP_DEFAUT = "https://www.mad-up.fr/update/PRO/Core/nrf52840_3_0_1.zip";
const CORE_DEFAUT = "3.0.1";

const Activation = () => {
  const { lang } = useParams();
  const L = LANGUES.includes(lang) ? lang : "en";
  const T = dataActivation.default[L];
  const location = useLocation();
  const navigate = useNavigate();

  // Parametres du QR : lus UNE fois, memorises, puis retires de la barre
  // d'adresse (historique, captures d'ecran, partage du lien).
  const [attente] = useState(() => {
    const q = new URLSearchParams(location.search);
    if (q.has("sn") || q.has("t")) return sauverActivation(q.get("sn"), q.get("t"));
    return lireActivation() || { sn: "", t: "" };
  });
  useEffect(() => {
    if (location.search) navigate(`/${L}/activer`, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [session, setSession] = useState(sessionAcademie);
  const [etape, setEtape] = useState(() => (sessionAcademie() ? "attente" : "connexion"));
  const [raison, setRaison] = useState("");
  const [expiree, setExpiree] = useState(false);
  const [appareil, setAppareil] = useState(null);   // {serial_num, zip_url, core_version, deja}
  const [mail, setMail] = useState("");             // "" | envoi | ok | ko

  const avecJeton = JETON_RE.test(attente.t || "") && Boolean(attente.sn);

  const rattacher = useCallback(() => {
    const s = sessionAcademie();
    if (!s) { setEtape("connexion"); return; }
    setEtape("attente");
    axios.post("/Back-end/claimDevice", { serial_num: attente.sn, jeton: attente.t },
               { headers: { Authorization: `Bearer ${s.token}` } })
      .then((r) => {
        const d = r.data || {};
        if (d.status === true) {
          effacerActivation();
          setAppareil({ serial_num: d.serial_num, zip_url: d.zip_url || ZIP_DEFAUT,
                        core_version: d.core_version || CORE_DEFAUT, deja: d.raison === "DEJA_FAIT" });
          setEtape("succes");
        } else {
          setRaison(d.raison || "ERREUR_BASE");
          setEtape("refus");
        }
      })
      .catch((e) => {
        const code = e.response ? e.response.status : 0;
        if (code === 401) {            // session inconnue ou remplacee par une autre connexion
          sessionStorage.removeItem("userData");
          setSession(null); setExpiree(true); setEtape("connexion");
          return;
        }
        setRaison(code === 429 ? "DEBIT" : (code ? "ERREUR_BASE" : "RESEAU"));
        setEtape("refus");
      });
  }, [attente.sn, attente.t]);

  useEffect(() => {
    if (!session) return;
    if (avecJeton) rattacher();
    else setEtape("manuel");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const seDeconnecter = () => {
    sessionStorage.removeItem("userData");
    navigate(`/${L}/connexion`);
  };

  const envoyerMail = () => {
    const s = sessionAcademie();
    if (!s || !appareil || mail === "envoi") return;
    setMail("envoi");
    // Logique EXISTANTE (boite a outils PRO) : sendCoreVersion joint le zip et la
    // procedure PDF. Le serveur choisit le fichier par la version (3.0.1).
    axios.post("/Back-end/sendCoreVersion", {
      device: { serial_num: appareil.serial_num, name: "PROCONNECT" },
      mail: dataSupport.default[L].mail,
      // Relecture (C3) : jamais l'objet de session entier (il contient le jeton de
      // session, que sendCoreFile ecrit dans backend.log) : e-mail et prenom seulement.
      auth: { email: s.email, first_name: s.first_name },
      lang: L,
      version: appareil.core_version,
    })
      .then((r) => setMail(typeof r.data === "string" && r.data.indexOf("succès") !== -1 ? "ok" : "ko"))
      .catch(() => setMail("ko"));
  };

  const meta = {
    title: T.meta.title, description: T.meta.description, canonical: T.meta.canonical,
    keywords: T.meta.keywords, image: T.meta.image,
  };

  return (
    <>
      <Meta meta={meta} />
      <Helmet>
        <meta name="referrer" content="no-referrer" />
        <meta name="robots" content="noindex" />
      </Helmet>
      <header>
        <MDBNavbar light bgColor="white" className="activer-nav">
          <MDBContainer fluid className="activer-nav__in">
            <MDBNavbarBrand href={`/${L}/`}>
              {/* Marque en texte : les logos images des autres pages
                  (mad-up.com/api/uploads/...) repondent 404 depuis le 29/09. */}
              <span className="activer-marque">MAD-UP <span>Academy</span></span>
            </MDBNavbarBrand>
            <div className="d-flex"><Language /></div>
          </MDBContainer>
        </MDBNavbar>
      </header>

      <main className="activer">
        {attente.sn && etape !== "succes" && (
          <p className="activer__sn">{T.serial} : <strong>{attente.sn}</strong></p>
        )}

        {etape === "connexion" && (
          <section className="activer__carte">
            <h1>{T.connexion.title}</h1>
            {expiree && <p className="activer__alerte">{T.connexion.sessionExpired}</p>}
            <p>{T.connexion.intro}</p>
            <Link className="activer__btn" to={`/${L}/connexion`}>{T.connexion.login}</Link>
            <Link className="activer__btn activer__btn--second" to={`/${L}/create-account`}>{T.connexion.create}</Link>
            <h2>{T.connexion.newAccountTitle}</h2>
            <ol className="activer__liste">
              {T.connexion.newAccountSteps.map((x, i) => <li key={i}>{x}</li>)}
            </ol>
          </section>
        )}

        {etape === "attente" && (
          <section className="activer__carte activer__carte--centre" aria-live="polite">
            <div className="activer__roue" aria-hidden="true"></div>
            <p>{T.loading}</p>
          </section>
        )}

        {etape === "refus" && (
          <section className="activer__carte" aria-live="polite">
            <h1>{T.errors.title}</h1>
            <p className="activer__alerte">{T.errors[raison] || T.errors.ERREUR_BASE}</p>
            {["ERREUR_BASE", "RESEAU", "DEBIT"].includes(raison) && (
              <button type="button" className="activer__btn" onClick={rattacher}>{T.errors.retry}</button>
            )}
            {["DEJA_RATTACHE", "NON_PROCONNECT", "INVALIDE"].includes(raison) && (
              <a className="activer__btn" href={SAV} target="_blank" rel="noopener noreferrer">{T.success.help}</a>
            )}
            {["INVALIDE", "JETON_INVALIDE", "MANQUANTS"].includes(raison) && (
              <button type="button" className="activer__btn activer__btn--second" onClick={() => setEtape("manuel")}>{T.errors.manual}</button>
            )}
            {raison === "DEJA_RATTACHE" && (
              <button type="button" className="activer__lien" onClick={seDeconnecter}>{T.errors.otherAccount}</button>
            )}
          </section>
        )}

        {etape === "manuel" && (
          <AjoutManuel T={T} snInitial={SERIE_PROCONNECT_RE.test(attente.sn || "") ? attente.sn : ""}
                       onSucces={(sn) => {
                         effacerActivation();
                         setAppareil({ serial_num: sn, zip_url: ZIP_DEFAUT, core_version: CORE_DEFAUT, deja: false });
                         setEtape("succes");
                       }} />
        )}

        {etape === "succes" && appareil && (
          <Procedure T={T} L={L} appareil={appareil} mail={mail} envoyerMail={envoyerMail} />
        )}
      </main>
    </>
  );
};

const Procedure = ({ T, L, appareil, mail, envoyerMail }) => {
  const S = T.success;
  return (
    <>
      <section className="activer__carte activer__carte--ok">
        <h1><i className="fa-solid fa-circle-check" aria-hidden="true"></i> {S.title}</h1>
        <p className="activer__sn activer__sn--carte">{T.serial} : <strong>{appareil.serial_num}</strong></p>
        {appareil.deja && <p>{S.already}</p>}
      </section>

      <section className="activer__carte activer__carte--attention">
        <h2>{S.warningTitle}</h2>
        <p>{S.warning}</p>
      </section>

      <section className="activer__carte">
        <h2>{S.step1Title}</h2>
        <p>{S.step1Text}</p>
        <a className="activer__btn" href={appareil.zip_url} download>
          <i className="fa-solid fa-download" aria-hidden="true"></i> {S.download}
        </a>
        <button type="button" className="activer__btn activer__btn--second" onClick={envoyerMail}
                disabled={mail === "envoi" || mail === "ok"}>
          <i className="fa-regular fa-envelope" aria-hidden="true"></i> {mail === "envoi" ? S.mailSending : S.mail}
        </button>
        {mail === "ok" && <p className="activer__info" aria-live="polite">{S.mailOk}</p>}
        {mail === "ko" && <p className="activer__alerte" aria-live="polite">{S.mailKo}</p>}
      </section>

      <section className="activer__carte">
        <h2>{S.step2Title}</h2>
        <a className="activer__btn activer__btn--app" href={APP_IOS} target="_blank" rel="noopener noreferrer">
          <i className="fa-brands fa-apple" aria-hidden="true"></i> {S.ios}
        </a>
        <a className="activer__btn activer__btn--app" href={APP_ANDROID} target="_blank" rel="noopener noreferrer">
          <i className="fa-brands fa-google-play" aria-hidden="true"></i> {S.android}
        </a>
      </section>

      <section className="activer__carte">
        <h2>{S.step3Title}</h2>
        <p>{S.step3Text}</p>
      </section>

      <section className="activer__carte">
        <h2>{S.step4Title}</h2>
        <p>{S.step4Text}</p>
        <p className="activer__info">{S.step4Tip}</p>
      </section>

      <section className="activer__carte">
        <h2>{S.step5Title}</h2>
        <ol className="activer__liste">
          {S.step5List.map((x, i) => <li key={i}>{x}</li>)}
        </ol>
      </section>

      <section className="activer__carte">
        <a className="activer__btn activer__btn--second" href={VIDEO} target="_blank" rel="noopener noreferrer">
          <i className="fa-brands fa-youtube" aria-hidden="true"></i> {S.video}
        </a>
        <Link className="activer__btn activer__btn--second" to={`/${L}/MyDevice`}>{S.myDevices}</Link>
        <a className="activer__lien" href={SAV} target="_blank" rel="noopener noreferrer">{S.help}</a>
      </section>
    </>
  );
};

// Parcours degrade (QR sans jeton) : la logique EXISTANTE d'ajout manuel
// (POST /Back-end/addDevice, memes champs que la fenetre « Ajouter un dispositif »).
const AjoutManuel = ({ T, snInitial, onSucces }) => {
  const M = T.manual;
  const [sn, setSn] = useState(snInitial);
  const [revendeur, setRevendeur] = useState("");
  const [date, setDate] = useState("");
  const [erreur, setErreur] = useState("");
  const [envoi, setEnvoi] = useState(false);

  const valider = (e) => {
    e.preventDefault();
    const auth = sessionAcademie();
    const serie = sn.trim().toUpperCase();
    if (!serie || !revendeur.trim() || !date) { setErreur(M.required); return; }
    if (!SERIE_PROCONNECT_RE.test(serie)) { setErreur(M.badSerial); return; }
    if (!auth) { window.location.reload(); return; }
    setEnvoi(true); setErreur("");
    axios.post("/Back-end/addDevice", {
      name: "PROCONNECT", serial_num: serie, name_distributer: revendeur.trim(), date_purchase: date,
      user_id: auth.user_id, firstName: auth.first_name, lastName: auth.last_name,
      email: auth.email, id_sellsy: auth.id_sellsy,
    })
      .then((r) => {
        const d = r.data || {};
        if (d.status === true) { onSucces(serie); return; }
        setErreur(d.message === "dispositif inexistant" ? M.unknown : M.otherAccount);
      })
      .catch(() => setErreur(T.errors.RESEAU))
      .finally(() => setEnvoi(false));
  };

  return (
    <section className="activer__carte">
      <h1>{M.title}</h1>
      <p>{M.intro}</p>
      <form onSubmit={valider} noValidate>
        <label htmlFor="act-sn">{M.serial}</label>
        <input id="act-sn" value={sn} onChange={(e) => setSn(e.target.value)} autoCapitalize="characters"
               autoComplete="off" placeholder="CA09260123" maxLength={10} />
        <label htmlFor="act-rev">{M.distributor}</label>
        <input id="act-rev" value={revendeur} onChange={(e) => setRevendeur(e.target.value)} />
        <label htmlFor="act-date">{M.date}</label>
        <input id="act-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        {erreur && <p className="activer__alerte" aria-live="polite">{erreur}</p>}
        <button type="submit" className="activer__btn" disabled={envoi}>{M.submit}</button>
      </form>
    </section>
  );
};

export default Activation;
